/**
 * Clinic-logo helper — single source of truth for how every printable
 * document in AUDINEXA fetches the tenant's uploaded logo.
 *
 * The logo lives in GridFS bucket `clinic_logos` and is streamed via
 * `GET /api/settings/clinic/logo`. This module wraps that endpoint
 * with a memoised blob→data-URL fetch so:
 *
 *   1. Popup print windows (thermal receipt) can embed the image
 *      inline as a data-URL without a same-origin cookie race.
 *   2. Repeated calls in the same session hit an in-memory cache
 *      instead of re-downloading — the invoice page prints ~4×
 *      before the operator switches patients.
 *   3. Missing logo (never uploaded, 404) is a silent no-op so the
 *      caller can render a plain header without a broken `<img>`.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache. Keyed by nothing — every tab is scoped to a
// single clinic, and re-uploading the logo bumps the cache-buster on
// the settings page. Print flows do not run concurrently with an
// upload for the same operator.
let _dataUrlPromise = null;
let _blobUrl = null;

async function _blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Returns a `data:image/…` URL for the current clinic's logo, or
 * `null` if the clinic has not uploaded one (or the fetch fails).
 * Never throws — printing is a UX flow, not a critical path.
 */
export async function getClinicLogoDataUrl() {
  if (_dataUrlPromise) return _dataUrlPromise;
  _dataUrlPromise = (async () => {
    try {
      const r = await axios.get(`${API}/settings/clinic/logo`, {
        responseType: 'blob',
        // Suppress console-level Axios error noise when the tenant
        // has no logo on file yet (404 is expected, not an error).
        validateStatus: (s) => s === 200 || s === 404,
      });
      if (r.status !== 200 || !r.data || r.data.size === 0) return null;
      return await _blobToDataUrl(r.data);
    } catch {
      return null;
    }
  })();
  return _dataUrlPromise;
}

/**
 * Blob URL variant for `<img src>` on-screen. Faster than a data URL
 * for on-screen rendering but cannot survive a `window.open` handoff.
 * Automatically revoked on page-unload.
 */
export function getClinicLogoBlobUrl() {
  if (_blobUrl) return Promise.resolve(_blobUrl);
  return (async () => {
    try {
      const r = await axios.get(`${API}/settings/clinic/logo`, {
        responseType: 'blob',
        validateStatus: (s) => s === 200 || s === 404,
      });
      if (r.status !== 200 || !r.data || r.data.size === 0) return null;
      _blobUrl = URL.createObjectURL(r.data);
      return _blobUrl;
    } catch {
      return null;
    }
  })();
}

/** Wipes the memo. Called by ClinicDetailsTab after a fresh logo upload. */
export function clearClinicLogoCache() {
  _dataUrlPromise = null;
  if (_blobUrl) {
    try { URL.revokeObjectURL(_blobUrl); } catch { /* noop */ }
    _blobUrl = null;
  }
}
